import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KycProofUploader } from "@/components/kyc/KycProofUploader";
import { KycComplementForm } from "@/components/KycComplementForm";
import { VerifiedSubscriptionCard, useVerifiedSubscription } from "@/components/VerifiedSubscriptionCard";
import { toast } from "sonner";
import { ShieldCheck, Loader2, LinkIcon, X, Check, Clock, Mic, Music, Sparkles } from "lucide-react";
import { stepBenefits } from "@/lib/privileges";
import { publicOrigin } from "@/lib/publicOrigin";
import { purchaseChannel, fmtMoney } from "@/lib/payments";
import { buyStoreProduct, restoreStorePurchases } from "@/lib/iap";
import { PurchaseFailedCard } from "@/components/PurchaseFailedCard";
import { useNavigate } from "react-router-dom";

const CREATOR_TYPES = ["preacher", "artist", "influencer"];
const CREATOR_ICON = { preacher: Mic, artist: Music, influencer: Sparkles };
const CREATOR_FB = { preacher: "Prédicateur", artist: "Artiste", influencer: "Influenceur(e)" };

// Yalola Vérifié 1.5.2 — ONE yearly subscription (Web = Stripe, Android = Google Play,
// iOS = App Store via RevenueCat). Creator roles are validated for free by an admin (KYC).
export const VerifiedBadgeModal = ({ open, onOpenChange, defaultType }) => {
  const { config, refreshMe, user, t, lang } = useApp();
  const navigate = useNavigate();
  const payments = config?.payments || {};
  const { data: verif, reload } = useVerifiedSubscription(open && !!user);
  const price = verif?.price ?? payments.verified_price ?? 9.99;
  const cur = verif?.currency || payments.currency || config?.settings?.default_currency || "EUR";
  const { channel, store } = purchaseChannel(payments);
  const [busy, setBusy] = useState(null);
  const pollRef = useRef(null);

  const [mine, setMine] = useState([]);
  const loadMine = () => api.get("/kyc/mine").then(({ data }) => setMine(data.requests || [])).catch(() => setMine([]));
  useEffect(() => {
    if (open && user) loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const infoReq = mine.find((r) => r.status === "info_requested");
  const kycPending = mine.find((r) => r.status === "pending");

  const creatorRole = CREATOR_TYPES.includes(defaultType) ? defaultType : (CREATOR_TYPES.includes(user?.requested_role) ? user.requested_role : null);
  const showCreator = !!creatorRole && user?.role === "user";
  const [proofLink, setProofLink] = useState("");
  const [proofDocs, setProofDocs] = useState([]);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const pollStatus = (sessionId) => {
    stopPoll();
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          stopPoll(); setBusy(null);
          await refreshMe?.(); await reload();
          toast.success(t("verified.activated_toast", "Yalola Vérifié activé ✓"));
          onOpenChange(false);
          if (user?.id) navigate(`/profile/${user.id}`);
        }
      } catch { /* keep polling */ }
      if (tries > 80) { stopPoll(); setBusy(null); }
    }, 2500);
  };

  const [failure, setFailure] = useState(null);
  // « Déjà abonné » sur un autre compte : si c'est bien ce membre qui a payé, la restauration lui rend le badge (sinon rien ne bouge).
  const restoreOwn = async () => {
    const r = await restoreStorePurchases({ payments, userId: user?.id });
    if (r?.verified) { await refreshMe?.(); await reload(); toast.success(t("verified.activated_toast", "Yalola Vérifié activé ✓")); onOpenChange(false); return true; }
    return false;
  };
  const subscribe = async () => {
    setBusy("sub");
    setFailure(null);
    if (channel === "store") {
      try {
        const r = await buyStoreProduct({ payments, userId: user?.id, kind: "verified_yearly" });
        if (!r.cancelled) { await refreshMe?.(); await reload(); toast.success(t("verified.activated_toast", "Yalola Vérifié activé ✓")); onOpenChange(false); }
      } catch (e) { setFailure({ error: e, diagnosticId: e?.diagnosticId }); }
      setBusy(null);
      return;
    }
    const popup = window.open("", "_blank"); // sync open to beat popup blockers (web)
    try {
      const { data } = await api.post("/payments/verification/checkout", { plan: "yearly", origin_url: publicOrigin(), native_app: false });
      if (popup && !popup.closed) {
        popup.location = data.checkout_url;
        toast.success(t("verified.popup_toast", "Paiement ouvert dans une fenêtre — reviens ici, tout se met à jour automatiquement 🙏"));
        pollStatus(data.session_id);
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      try { popup && popup.close(); } catch { /* noop */ }
      toast.error(e?.friendlyMessage || t("common.error", "Erreur"));
      setBusy(null);
    }
  };

  const submitKyc = async () => {
    if (!proofLink.trim() && proofDocs.length === 0) {
      toast.error(t("verified.proof_required", "Ajoute une preuve d'activité : un lien OU un document (au moins un des deux)."));
      return;
    }
    setBusy("kyc");
    try {
      await api.post("/kyc/submit", { requested_role: creatorRole, website: proofLink.trim() || undefined, documents: proofDocs });
      toast.success(t("verified.kyc_sent", "Demande envoyée ✓ — un administrateur valide ta qualité, tu seras notifié."));
      await loadMine(); await refreshMe?.();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(null); }
  };

  const roleLabel = creatorRole ? t(`role.${creatorRole}`, CREATOR_FB[creatorRole]) : "";
  const RoleIcon = creatorRole ? CREATOR_ICON[creatorRole] : Mic;
  const isLive = !!verif?.is_verified && !!verif?.subscription && ["active", "cancelled_active_until", "past_due"].includes(verif.subscription.status);
  // Perks = what the badge REALLY unlocks for this member under Admin › Privilèges rules (backend resolver) + the
  // always-true lines (badge display, trust). Falls back to the generic list when the member payload has no hints.
  const badgeStep = stepBenefits(user?.privileges, "badge", t);
  const benefits = [
    t("verified.b1", "Badge vérifié ✓ à côté de ton nom, partout sur Yalola"),
    ...(user?.privileges?.hints ? [...badgeStep.lines, ...badgeStep.money.map((m) => m.text)] : [t("verified.b2", "Directs plus longs — durée étendue réservée aux comptes vérifiés")]),
    t("verified.b3", "Confiance et visibilité accrues auprès de la communauté"),
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopPoll(); onOpenChange(o); }}>
      <DialogContent aria-describedby={undefined} variant="sheet" className="sm:max-w-[480px]" data-testid="verified-badge-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck size={20} style={{ color: "var(--yalola-accent)" }} /> {t("verified.title", "Yalola Vérifié")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {infoReq && (
            <KycComplementForm req={infoReq} onDone={async () => { await refreshMe?.(); await loadMine(); onOpenChange(false); }} />
          )}

          {showCreator && !infoReq && (
            <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="verified-creator-section">
              <div className="flex items-center gap-2 text-sm font-semibold"><RoleIcon size={16} style={{ color: "var(--yalola-primary)" }} /> {t("verified.creator_title", "Rôle {role} — validation gratuite").replace("{role}", roleLabel)}</div>
              {kycPending ? (
                <div className="rounded-xl p-3 text-sm flex items-start gap-2" style={{ background: "#D1FAE5", color: "#065F46" }} data-testid="verified-kyc-pending">
                  <Check size={16} className="shrink-0 mt-0.5" />
                  <span>{t("verified.kyc_pending", "Ta preuve est en cours d'examen par un administrateur. Tu seras notifié dès validation.")}</span>
                </div>
              ) : (
                <>
                  <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
                    {t("verified.creator_hint", "Un lien (chaîne, page, réseau) OU un document (photo/capture). Un admin la vérifie puis active ton rôle — sans paiement.")}
                  </p>
                  <div>
                    <Label className="text-xs flex items-center gap-1"><LinkIcon size={12} /> {t("verified.proof_link", "Lien")}</Label>
                    <Input value={proofLink} onChange={(e) => setProofLink(e.target.value)} placeholder="https://youtube.com/@ton-ministere" data-testid="verified-proof-link" />
                  </div>
                  <div>
                    <Label className="text-xs">{t("verified.proof_doc", "Document (image)")}</Label>
                    <KycProofUploader docs={proofDocs} onChange={setProofDocs} buttonLabel={t("verified.proof_add", "Ajouter un justificatif")} testId="verified-proof" />
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={submitKyc} disabled={!!busy} data-testid="verified-kyc-submit">
                    {busy === "kyc" ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />} {t("verified.kyc_submit", "Envoyer ma preuve")}
                  </Button>
                </>
              )}
            </div>
          )}

          {isLive ? (
            <VerifiedSubscriptionCard data={verif} onChanged={reload} />
          ) : (
            <>
              <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
                {t("verified.intro", "Un seul abonnement, le même sur le web, Android et iOS. Le badge s'affiche à côté de ton nom partout sur Yalola.")}
              </p>
              <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--yalola-primary-soft)" }} data-testid="verified-benefits">
                {benefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-2 text-[13px]">
                    <Check size={15} className="mt-0.5 shrink-0" style={{ color: "var(--yalola-primary)" }} />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 border rounded-xl p-3.5" style={{ borderColor: "var(--yalola-primary)" }} data-testid="badge-plan-yearly">
                <div>
                  <div className="font-semibold text-sm">{t("verified.plan_yearly", "Abonnement annuel")} — {fmtMoney(price, cur, lang)}{t("verified.per_year", "/ an")}</div>
                  <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("verified.plan_hint", "Renouvelé automatiquement chaque année, annulable à tout moment.")}</div>
                </div>
                {channel === "unavailable" ? (
                  <span className="chip text-[10px] shrink-0 inline-flex items-center gap-1" data-testid="badge-store-soon"><Clock size={11} /> {t("verified.store_soon", "Bientôt disponible")}</span>
                ) : (
                  <Button size="sm" className="pill-primary rounded-full shrink-0" onClick={subscribe} disabled={!!busy || verif?.enabled === false} data-testid="badge-buy-yearly">
                    {busy === "sub" ? <Loader2 size={14} className="animate-spin" /> : t("verified.choose", "S'abonner")}
                  </Button>
                )}
              </div>
              {failure && <PurchaseFailedCard failure={failure} busy={!!busy} onRetry={subscribe} onRestore={restoreOwn} onClose={() => setFailure(null)} />}
              {verif?.enabled === false && (
                <p className="text-xs" style={{ color: "#b45309" }} data-testid="verified-disabled">{t("verified.disabled", "L'abonnement Yalola Vérifié est temporairement indisponible.")}</p>
              )}
              <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="verified-channel-note">
                {channel === "store"
                  ? t("verified.pay_store", "Achat via {store} : l'abonnement se gère depuis ton compte {store}.").replace(/\{store\}/g, store || "")
                  : channel === "unavailable"
                  ? t("verified.store_soon_body", "L'abonnement arrive bientôt dans l'app via {store}.").replace("{store}", store || "")
                  : t("verified.pay_stripe", "Paiement sécurisé par Stripe. Le paiement s'ouvre dans une fenêtre pour ne pas quitter la page.")}
              </p>
              {/* App Store Review 3.1.2 : liens fonctionnels vers les CGU et la Politique de confidentialité dans le parcours d'achat. */}
              <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="verified-legal-links">
                <a href="/legal/terms" target="_blank" rel="noreferrer" className="underline" data-testid="verified-terms-link">{t("verified.legal_terms", "Conditions d'utilisation")}</a>
                {" · "}
                <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline" data-testid="verified-privacy-link">{t("verified.legal_privacy", "Politique de confidentialité")}</a>
              </p>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
